import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Users,
  Spinner,
  MagnifyingGlass,
  Wallet,
  House,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTenantsPage = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/tenants`);
      setTenants(res.data);
    } catch (error) {
      toast.error("Kiracılar yuklenirken hata olustu");
    } finally {
      setLoading(false);
    }
  };

  const filtered = tenants.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.name?.toLowerCase().includes(q) ||
      t.email?.toLowerCase().includes(q) ||
      t.customer_name?.toLowerCase().includes(q)
    );
  });

  const totalBalance = tenants.reduce((s, t) => s + (t.balance || 0), 0);
  const zeroBalance = tenants.filter((t) => (t.balance || 0) <= 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-tenants-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Tum Kiracılar</h1>
          <p className="text-slate-500 mt-1">Sistemdeki tum kiracilar ve bakiye bilgileri</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Toplam Kiraci</p>
              <p className="text-2xl font-bold text-slate-900">{tenants.length}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Users size={20} weight="duotone" className="text-indigo-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Toplam Bakiye</p>
              <p className="text-2xl font-bold text-slate-900">{"\u20ba"}{totalBalance.toFixed(2)}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Wallet size={20} weight="duotone" className="text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Bakiyesi Olan</p>
              <p className="text-2xl font-bold text-emerald-600">{tenants.length - zeroBalance}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Wallet size={20} weight="duotone" className="text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">{t("devices.low_balance")}</p>
              <p className="text-2xl font-bold text-red-600">{zeroBalance}</p>
            </div>
            <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
              <Wallet size={20} weight="duotone" className="text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Kiraci veya musteri ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="tenant-search"
        />
      </div>

      {/* Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{search ? "Aramayla esleyen kiraci yok" : "Henuz kiraci yok"}</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t("admin.tenant_header")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("common.email")}</TableHead>
                    <TableHead>{t("admin.customer_owner")}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t("admin.assigned_units")}</TableHead>
                    <TableHead>{t("common.balance")}</TableHead>
                    <TableHead className="hidden md:table-cell">{t("common.register_date")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice((page - 1) * pageSize, page * pageSize).map((tenant) => (
                    <TableRow key={tenant.id} data-testid={`tenant-row-${tenant.id}`}>
                      <TableCell className="font-medium">{tenant.name}</TableCell>
                      <TableCell className="text-sm text-slate-500 hidden md:table-cell">{tenant.email}</TableCell>
                      <TableCell>
                        {tenant.customer_name ? (
                          <span className="text-sm">{tenant.customer_name}</span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {tenant.unit_names && tenant.unit_names.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {tenant.unit_names.map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                <House size={12} className="mr-1" />
                                {name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">{t("devices.unassigned")}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          (tenant.balance || 0) > 50
                            ? "bg-emerald-100 text-emerald-700"
                            : (tenant.balance || 0) > 0
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                        }>
                          {"\u20ba"}{(tenant.balance || 0).toFixed(2)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 hidden md:table-cell">
                        {new Date(tenant.created_at).toLocaleDateString("tr-TR")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {filtered.length > pageSize && (
              <div className="p-4">
                <Pagination total={filtered.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
              </div>
            )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTenantsPage;
